import { ThemeEvents } from '@theme/events';

class NotifyMeForm extends HTMLElement {
  #variantId = null;
  #ac = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    this.querySelector('.notify-me__trigger')
      ?.addEventListener('click', () => this.#openDialog(), { signal });

    this.querySelector('.notify-me__close')
      ?.addEventListener('click', () => this.#closeDialog(), { signal });

    this.querySelector('.notify-me__dialog')
      ?.addEventListener('click', (e) => {
        if (e.target === e.currentTarget) this.#closeDialog();
      }, { signal });

    this.querySelector('.notify-me__form')
      ?.addEventListener('submit', this.#onSubmit, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #productId() { return this.dataset.productId; }
  get #productTitle() { return this.dataset.productTitle; }
  get #dialog() { return this.querySelector('.notify-me__dialog'); }

  #onVariantUpdate = (event) => {
    const { resource, data } = event.detail;
    if (data?.productId !== this.#productId) return;
    if (resource == null) return;

    if (!resource.available) {
      this.#variantId = resource.id;
      const sizeName = this.#readSizeName();
      this.#showUnavailable(sizeName);
    } else {
      this.#variantId = null;
      this.#closeDialog();
      this.#showDefault();
    }
  };

  #readSizeName() {
    const picker = document.querySelector(`variant-picker[data-product-id="${this.#productId}"]`);
    return [...(picker?.querySelectorAll('input[data-current-checked="true"]') || [])]
      .map(i => i.value).filter(Boolean).join(' / ');
  }

  #showDefault() {
    this.querySelector('.notify-me__hint')?.removeAttribute('hidden');
    this.querySelector('.notify-me__selected')?.setAttribute('hidden', '');
  }

  #showUnavailable(sizeName) {
    const hint = this.querySelector('.notify-me__hint');
    const selected = this.querySelector('.notify-me__selected');
    const sizeText = this.querySelector('.notify-me__size-text');
    const dialogTitle = this.querySelector('.notify-me__dialog-title');

    if (hint) hint.setAttribute('hidden', '');
    if (sizeText) sizeText.textContent = `Size ${sizeName} unavailable`;
    if (dialogTitle) dialogTitle.textContent = `Size ${sizeName} is currently unavailable`;
    if (selected) selected.removeAttribute('hidden');

    // Sync hidden form fields
    const tags = this.querySelector('.notify-me__tags-input');
    const note = this.querySelector('.notify-me__note-input');
    if (tags) tags.value = `notify-variant-${this.#variantId},back-in-stock,product-${this.#productId}`;
    if (note) note.value = `Back in stock request for ${this.#productTitle} / ${sizeName}`;
  }

  #openDialog() {
    const dialog = this.#dialog;
    if (!dialog) return;
    // Reset to form state each open
    const form = this.querySelector('.notify-me__form');
    const success = this.querySelector('.notify-me__success');
    const error = this.querySelector('.notify-me__error');
    if (form) form.hidden = false;
    if (success) success.hidden = true;
    if (error) error.textContent = '';
    dialog.showModal();
  }

  #closeDialog() {
    this.#dialog?.close();
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const email = form.querySelector('input[type="email"]')?.value?.trim();
    if (!email) return;

    const btn = form.querySelector('[type="submit"]');
    if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
    const error = this.querySelector('.notify-me__error');
    if (error) error.textContent = '';

    const body = new URLSearchParams({
      form_type: 'customer',
      utf8: '✓',
      'contact[email]': email,
      'contact[accepts_marketing]': 'true',
      'contact[tags]': form.querySelector('.notify-me__tags-input')?.value ?? '',
      'contact[note]': form.querySelector('.notify-me__note-input')?.value ?? '',
    });

    try {
      await fetch('/contact#contact_form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
        redirect: 'manual',
      });
      form.hidden = true;
      const success = this.querySelector('.notify-me__success');
      if (success) success.hidden = false;
    } catch {
      if (error) error.textContent = 'Something went wrong — please try again.';
      if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
    }
  };
}

if (!customElements.get('notify-me-form')) {
  customElements.define('notify-me-form', NotifyMeForm);
}
